// @flow
import itemsAdded from 'app/common/redux/actions/feeds/itemsAdded';
import itemsRemoved from 'app/common/redux/actions/feeds/itemsRemoved';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { Thunk } from 'app/common/redux/types';
import UserDidItDataActionTypes from 'app/common/redux/actions/user_did_it_data/actionTypes';
import { FEED_TYPE } from 'app/common/redux/reducers/feeds';
import { pinDidIt, pinUnDidIt } from 'app/common/redux/actions/pin/didIt';
import { resourceFetch, type ResourceFetch } from 'app/common/redux/actions/resourceFetch';

export type UserDidItDataCreateComplete = {
  type: typeof UserDidItDataActionTypes.USER_DID_IT_DATA_CREATE,
  payload: {
    user_did_it_data: {
      id: string,
      images: Array<Object>,
      details: string,
    },
  },
};

function createComplete(user_did_it_data: Object): UserDidItDataCreateComplete {
  return {
    type: UserDidItDataActionTypes.USER_DID_IT_DATA_CREATE,
    payload: {
      user_did_it_data,
    },
  };
}

export type UserDidItDataUpdateComplete = {
  type: typeof UserDidItDataActionTypes.USER_DID_IT_DATA_UPDATE,
  payload: {
    user_did_it_data: Object,
  },
};

function updateComplete(user_did_it_data: Object): UserDidItDataUpdateComplete {
  return {
    type: UserDidItDataActionTypes.USER_DID_IT_DATA_UPDATE,
    payload: {
      user_did_it_data,
    },
  };
}

export type UserDidItDataDeleteComplete = {
  type: typeof UserDidItDataActionTypes.USER_DID_IT_DATA_DELETE,
  payload: {
    id: string,
  },
};

function deleteComplete(id: string): UserDidItDataDeleteComplete {
  return {
    type: UserDidItDataActionTypes.USER_DID_IT_DATA_DELETE,
    payload: {
      id,
    },
  };
}

export type UserDidItCreateData = {
  aggregatedPinDataId: string,
  details: string,
  fromUnifiedComment?: boolean,
  image_signatures: Array<string>,
  pin_id: string,
  publishFacebook: boolean,
  username: string,
};

// TODO(xixia): Add tags for user did its
export function createUserDidItData({
  aggregatedPinDataId,
  details,
  fromUnifiedComment,
  image_signatures,
  pin_id,
  publishFacebook,
  username,
}: UserDidItCreateData): Thunk<void> {
  const options = {
    aggregatedPinDataId,
    details,
    image_signatures,
    pin_id,
    publish_facebook: publishFacebook,
  };
  return (dispatch) =>
    ResourceFactory.create('DidItActivityResource', options)
      .callCreate()
      .then(
        (response) => {
          const userDidItData = response.resource_response.data;
          dispatch(pinDidIt(pin_id));
          dispatch(createComplete(userDidItData));
          dispatch(
            itemsAdded({
              feedId: aggregatedPinDataId,
              feedType: fromUnifiedComment
                ? FEED_TYPE.UNIFIED_COMMENTS
                : FEED_TYPE.USER_DID_IT_DATA,
              itemIds: [userDidItData.id],
              itemType: userDidItData.type,
            }),
          );
          dispatch(
            itemsAdded({
              feedId: username,
              feedType: FEED_TYPE.USER_DID_IT_DATA,
              itemIds: [userDidItData.id],
              itemType: userDidItData.type,
            }),
          );
        },
        (error) => {},
      );
}

export type UserDidItDeleteData = {
  aggregatedPinDataId: string,
  feedType?: 'unifiedComments' | 'userDidItData',
  id: string,
  isOwnedByMe: boolean,
  pinId: string,
  username: string,
};

export function deleteUserDidItData({
  aggregatedPinDataId,
  feedType,
  id,
  isOwnedByMe,
  pinId,
  username,
}: UserDidItDeleteData): Thunk<void> {
  return (dispatch) => {
    ResourceFactory.create('DidItActivityResource', {
      user_did_it_data_id: id,
    })
      .callDelete()
      .then(
        (response) => {
          if (isOwnedByMe) {
            dispatch(pinUnDidIt(pinId));
          }
          dispatch(deleteComplete(id));
          dispatch(
            itemsRemoved({
              feedId: aggregatedPinDataId,
              feedType: feedType || FEED_TYPE.USER_DID_IT_DATA,
              itemIds: [id],
              itemType: 'userdiditdata',
            }),
          );
          dispatch(
            itemsRemoved({
              feedId: username,
              feedType: FEED_TYPE.USER_DID_IT_DATA,
              itemIds: [id],
              itemType: 'userdiditdata',
            }),
          );
        },
        (error) => {},
      );
  };
}

export type UpdateUserDidItDataCommentCount = {
  type: typeof UserDidItDataActionTypes.USER_DID_IT_DATA_UPDATE_COMMENT_COUNT,
  payload: {
    id: string,
    increment: 1 | -1,
  },
};

export function updateUserDidItDataCommentCount(
  id: string,
  increment: 1 | -1,
): UpdateUserDidItDataCommentCount {
  return {
    type: UserDidItDataActionTypes.USER_DID_IT_DATA_UPDATE_COMMENT_COUNT,
    payload: {
      id,
      increment,
    },
  };
}

export function fetchUserDidItData(id: string): ResourceFetch {
  return resourceFetch('DidItActivityResource', {
    user_did_it_data_id: id,
  });
}

export type UserDidItUpdateData = {
  id: string,
  details: string,
  image_signatures: Array<string>,
  pinId: string,
};

export function updateUserDidItData({
  id,
  details,
  image_signatures,
  pinId,
}: UserDidItUpdateData): Thunk<void> {
  const options = {
    details,
    image_signatures,
    user_did_it_data_id: id,
    pin_id: pinId,
  };
  return (dispatch) => {
    ResourceFactory.create('DidItActivityResource', options)
      .callUpdate()
      .then(
        (response) => {
          const user_did_it_data = response.resource_response.data;
          dispatch(updateComplete(user_did_it_data));
        },
        (error) => {},
      );
  };
}

export function reportUserDidItData(
  aggregatedPinDataId: string,
  detailedReasons: ?Array<?string>,
  id: string,
  reason: ?string,
  username: string,
): Thunk<void> {
  const options = {
    reason,
    detailed_reasons: detailedReasons,
    user_did_it_data_id: id,
  };
  return (dispatch) => {
    ResourceFactory.create('DidItActivityFlagResource', options)
      .callCreate()
      .then(
        () => {
          dispatch(deleteComplete(id));
          dispatch(
            itemsRemoved({
              feedId: aggregatedPinDataId,
              feedType: FEED_TYPE.USER_DID_IT_DATA,
              itemIds: [id],
              itemType: 'userdiditdata',
            }),
          );
          dispatch(
            itemsRemoved({
              feedId: username,
              feedType: FEED_TYPE.USER_DID_IT_DATA,
              itemIds: [id],
              itemType: 'userdiditdata',
            }),
          );
        },
        (error) => {},
      );
  };
}
